import { useAuth } from '@commander/auth';
import {
  Badge,
  Button,
  cn,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  SearchIcon,
  Separator,
  Skeleton,
} from '@commander/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SearchResult } from '../search.types';
import { getSearchPageHref, openSearchResult } from '../searchNavigation';
import { useUniversalSearch } from '../useUniversalSearch';
import { SearchEntityIcon } from './SearchEntityIcon';

function ResultSummary({ result }: { result: SearchResult }) {
  const detail =
    result.subtitle ||
    result.parentName ||
    result.metadata?.email ||
    result.metadata?.stageName ||
    result.metadata?.status;

  return (
    <span className="flex min-w-0 flex-1 items-center gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/50">
        <SearchEntityIcon entity={result.entity} className="h-4 w-4 text-muted-foreground" />
      </span>
      <span className="min-w-0 text-left">
        <span className="block truncate font-medium text-popover-foreground">{result.title}</span>
        {detail ? (
          <span className="block truncate text-xs text-muted-foreground">{detail}</span>
        ) : null}
      </span>
    </span>
  );
}

export interface UniversalSearchBarProps {
  placeholder?: string;
  showKbdShortcut?: boolean;
}

export function UniversalSearchBar({
  placeholder = 'Search accounts, contacts, deals, quotes',
  showKbdShortcut = false,
}: UniversalSearchBarProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputNavigationRef = useRef<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showKbdShortcut) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showKbdShortcut]);
  const { user } = useAuth();
  const companyId = user?.company_employee?.company?.id;
  const { query, setQuery, debouncedQuery, data, isLoading, isError } = useUniversalSearch(
    '',
    companyId,
    true
  );
  const isSearchPage = location.pathname === '/search';
  const searchPageParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchPageQuery = isSearchPage ? (searchPageParams.get('q') ?? '') : '';
  const trimmedQuery = query.trim();
  // Only show results when the fetched data matches the current trimmed query.
  // Comparing against trimmedQuery prevents showing stale results during debounce window.
  const isQuerySettled = data?.query === trimmedQuery;
  const groups = isQuerySettled ? (data?.groups ?? []) : [];
  const hasResults = groups.some((group) => group.results.length > 0);
  const hasSettledQuery = trimmedQuery.length >= 2 && trimmedQuery === debouncedQuery;
  // Treat the debounce window as loading: after the input crosses 2 characters but before
  // debouncedQuery catches up, the query is disabled (idle, so isLoading is false) and the
  // dropdown would otherwise render empty content — a visible flicker on the first query.
  const isDebouncing = trimmedQuery !== debouncedQuery;

  const fullResultsPath = useMemo(() => {
    const params = isSearchPage ? new URLSearchParams(location.search) : new URLSearchParams();
    params.set('q', trimmedQuery);
    params.set('page', '1');
    return getSearchPageHref(params);
  }, [isSearchPage, location.search, trimmedQuery]);

  useEffect(() => {
    if (!isSearchPage) {
      searchInputNavigationRef.current = null;
      return;
    }
    if (searchInputNavigationRef.current === location.search) {
      searchInputNavigationRef.current = null;
      return;
    }
    setQuery(searchPageQuery);
  }, [isSearchPage, location.search, searchPageQuery, setQuery]);

  const updateQuery = (value: string) => {
    setQuery(value);
    if (!isSearchPage) return;

    const next = new URLSearchParams(location.search);
    const nextQuery = value.trim();
    if (nextQuery) next.set('q', nextQuery);
    else next.delete('q');
    next.set('page', '1');
    const nextSearch = `?${next.toString()}`;
    searchInputNavigationRef.current = nextSearch;
    navigate(`/search${nextSearch}`, { replace: true });
  };

  const openResult = (result: SearchResult) => {
    setOpen(false);
    openSearchResult(result.route, navigate);
  };

  const openFullResults = () => {
    if (trimmedQuery.length < 2) return;
    setOpen(false);
    if (/^https?:\/\//i.test(fullResultsPath)) {
      window.location.href = fullResultsPath;
      return;
    }
    navigate(fullResultsPath);
  };

  // Clicking/focusing the search input must not dismiss the dropdown. With PopoverAnchor
  // (no toggle behaviour) those events land outside the content, so guard them here.
  const isEventInsideSearchBar = (event: { target: EventTarget | null }) =>
    event.target instanceof Node && Boolean(anchorRef.current?.contains(event.target));

  return (
    <div className="hidden min-w-0 flex-1 justify-center px-2 md:flex xl:px-4">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative min-w-0 w-full max-w-xl">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="search"
              aria-haspopup="dialog"
              aria-expanded={open}
              className={cn(
                'h-10 w-full pl-9 text-sm shadow-sm transition-colors',
                showKbdShortcut && 'pr-12',
                open && 'border-primary bg-popover text-popover-foreground ring-2 ring-primary/20'
              )}
              placeholder={placeholder}
              value={query}
              onChange={(event) => updateQuery(event.target.value)}
              onFocus={() => setOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setOpen(false);
                if (event.key === 'Enter' && isQuerySettled && !hasResults) openFullResults();
              }}
            />
            {showKbdShortcut && !query ? (
              <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border/60 bg-muted/50 px-1.5 py-0.5 font-mono text-[11px] font-medium text-muted-foreground select-none">
                ⌘K
              </kbd>
            ) : null}
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="center"
          sideOffset={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            if (isEventInsideSearchBar(event)) event.preventDefault();
          }}
          onFocusOutside={(event) => {
            if (isEventInsideSearchBar(event)) event.preventDefault();
          }}
          className="z-[80] max-h-[min(36rem,calc(100vh-5rem))] w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
        >
          <div className="flex max-h-[inherit] flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
            <div className="max-h-[min(26rem,calc(100vh-12rem))] overflow-y-auto overscroll-contain">
              {trimmedQuery.length < 2 ? (
                <div className="px-6 py-7 text-center text-sm font-medium text-muted-foreground">
                  Type 2 or more characters
                </div>
              ) : null}
              {trimmedQuery.length >= 2 && (isLoading || isDebouncing) ? (
                <div className="space-y-2 p-3">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-4/5" />
                </div>
              ) : null}
              {trimmedQuery.length >= 2 && isError && !isLoading && !isDebouncing ? (
                <div className="px-6 py-7 text-center text-sm font-medium text-muted-foreground">
                  Search is temporarily unavailable
                </div>
              ) : null}
              {hasSettledQuery && !isLoading && !isError && !hasResults ? (
                <div className="px-6 py-7 text-center text-sm font-medium text-muted-foreground">
                  No results found
                </div>
              ) : null}
              {groups.map((group) => (
                <div key={group.entity} className="overflow-hidden p-2 text-popover-foreground">
                  <div className="flex items-center justify-between gap-2 px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase text-muted-foreground">
                    <span>
                      {group.label} · {group.results.length}
                    </span>
                  </div>
                  {group.results.map((result) => (
                    <Button
                      variant="ghost"
                      type="button"
                      key={`${result.entity}:${result.id}`}
                      onClick={() => openResult(result)}
                      className="h-auto w-full cursor-default justify-start gap-2 rounded-md px-2 py-2.5 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                    >
                      <ResultSummary result={result} />
                      <Badge
                        variant="outline"
                        className="ml-auto shrink-0 bg-background/50 text-muted-foreground"
                      >
                        {result.label}
                      </Badge>
                    </Button>
                  ))}
                </div>
              ))}
            </div>
            {trimmedQuery.length >= 2 ? (
              <>
                <Separator />
                <div className="flex shrink-0 items-center justify-between gap-3 bg-muted/30 p-2">
                  <span className="truncate px-2 text-xs text-muted-foreground">
                    {trimmedQuery}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-primary hover:bg-primary/10 hover:text-primary"
                    onClick={openFullResults}
                  >
                    See all results for {trimmedQuery}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
